import { inject } from '@angular/core';
import {
  setError,
  setLoaded,
  setLoading,
  withCallState,
  withDevtools,
} from '@angular-architects/ngrx-toolkit';

import { tapResponse } from '@ngrx/operators';
import {
  patchState,
  signalStore,
  withHooks,
  withMethods,
  withProps,
} from '@ngrx/signals';
import {
  setEntities,
  upsertEntity,
  withEntities,
} from '@ngrx/signals/entities';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { distinctUntilChanged, exhaustMap, pipe, tap } from 'rxjs';

import { TerritoryService, TerritoryType } from '@core/territory.service';

// eslint-disable-next-line @typescript-eslint/naming-convention
export const TerritoryTypesStore = signalStore(
  { providedIn: 'root' },
  withDevtools('territoriesTypesStore'),
  withEntities<TerritoryType>(),
  withCallState(),
  withProps(() => ({
    territoryService: inject(TerritoryService),
  })),
  withMethods(({ territoryService, ...store }) => ({
    loadTerritoriesTypes: rxMethod(
      pipe(
        distinctUntilChanged(),
        tap(() => {
          patchState(store, setLoading());
        }),
        exhaustMap(() =>
          territoryService.loadTypes().pipe(
            tapResponse({
              complete: () => {
                patchState(store, setLoaded());
              },
              error: (error) => {
                patchState(store, setError(error));
              },
              next: (response) => {
                patchState(store, setEntities(response));
              },
            }),
          ),
        ),
      ),
    ),
    saveTerritoryType: rxMethod<number>(
      pipe(
        tap(() => {
          patchState(store, setLoading());
        }),
        exhaustMap((id) =>
          territoryService.patchType(id, store.entityMap()[id]).pipe(
            tapResponse({
              complete: () => {
                patchState(store, setLoaded());
              },
              error: (error) => {
                patchState(store, setError(error));
              },
              next: (response) => {
                patchState(store, upsertEntity(response));
              },
            }),
          ),
        ),
      ),
    ),
  })),
  withHooks({
    onInit: (store) => {
      store.loadTerritoriesTypes({});
    },
  }),
);
